import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"

// Rename / move / pin / delete for one file or folder - a list row, a grid
// card, or the preview pane's own heading (see _row_menu, _file_row,
// _file_card, _preview). Generic over kind the same way the old
// files_item_controller.js was: the URL and the move param name
// (user_folder_id for a file, parent_id for a folder) ride in as data
// attributes, and pin-related targets/actions are simply absent from a
// folder's markup (see _row_menu.html.slim) rather than branched on here.
export default class extends Controller {
    static targets = ["title", "renameInput"]
    static values = { url: String, moveParam: String, deleteConfirm: String, pinned: Boolean }

    editName(event) {
        event?.preventDefault()
        this.titleTarget.hidden = true
        this.renameInputTarget.hidden = false
        this.renameInputTarget.value = this.titleTarget.textContent.trim()
        this.renameInputTarget.focus()
        this.renameInputTarget.select()
    }

    cancelRename(event) {
        event?.preventDefault()
        this._closeRename()
    }

    renameKeydown(event) {
        if (event.key === "Enter") {
            event.preventDefault()
            this.saveRename()
        } else if (event.key === "Escape") {
            event.preventDefault()
            this.cancelRename()
        }
    }

    saveRename() {
        if (this.renameInputTarget.hidden) return // already closed by Enter's keydown handler

        const name = this.renameInputTarget.value.trim()
        const current = this.titleTarget.textContent.trim()
        this._closeRename()
        if (!name || name === current) return

        this._patch({ name }).then((data) => {
            if (data && data.ok) {
                const record = data.file || data.folder
                this.titleTarget.textContent = record ? record.name : name
            } else {
                window.alert((data && data.error) || "Could not rename that.")
            }
        })
    }

    // One menu item per destination folder (see _row_menu.html.slim's
    // folder_move_options loop) rather than the old <select> - the clicked
    // item's own data-folder-id carries where to.
    moveTo(event) {
        event.preventDefault()
        const folderId = event.currentTarget.dataset.folderId || ""
        const body = {}
        body[this.moveParamValue] = folderId || null

        this._patch(body).then((data) => {
            if (data && data.ok) {
                window.location.reload()
            } else {
                window.alert((data && data.error) || "Could not move that.")
            }
        })
    }

    // Reloads on success, same as #moveTo/#destroy - the row's own pin glyph
    // next to the filename (see _file_row.html.slim) is server-rendered from
    // UserFile#pinned?, and the Pinned shortcut's sidebar count is only
    // computed at page load, so a targeted DOM patch here would leave both
    // stale until the next navigation anyway.
    togglePin(event) {
        event?.preventDefault()
        this._patch({ pinned: !this.pinnedValue }).then((data) => {
            if (data && data.ok) {
                window.location.reload()
            } else {
                window.alert((data && data.error) || "Could not update that.")
            }
        })
    }

    destroy(event) {
        event?.preventDefault()
        if (!window.confirm(this.deleteConfirmValue || "Delete this? This can't be undone.")) return

        fetch(this.urlValue, {
            method: "DELETE",
            headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin"
        })
            .then((response) => response.json().catch(() => ({ ok: response.ok })))
            .then((data) => {
                if (data.ok !== false) {
                    window.location.reload()
                } else {
                    window.alert(data.error || "Could not delete that.")
                }
            })
            .catch(() => window.alert("Could not delete that - check your connection."))
    }

    _patch(body) {
        return fetch(this.urlValue, {
            method: "PATCH",
            headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin",
            body: JSON.stringify(body)
        })
            .then((response) => response.json())
            .catch(() => ({ ok: false, error: "Could not reach the server." }))
    }

    _closeRename() {
        this.renameInputTarget.hidden = true
        this.titleTarget.hidden = false
    }
}
